import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function CategoryDetail() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return <NotFound />;
  }

  const categoryArticles = articles.filter((a) => a.categoryId === category.id);

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${category.name} - 开云平台`}
        description={category.description}
        keywords={`开云${category.name},开云平台${category.name}`}
        canonicalUrl={`https://kaiyun.example.com/categories/${category.slug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumbs
              items={[
                { label: '首页', path: '/' },
                { label: '分类浏览', path: '/categories' },
                { label: category.name },
              ]}
            />
          </div>

          <section className="pb-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="mb-12 p-8 rounded-xl bg-accent-50 border border-accent-200/50">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-14 h-14 rounded-xl bg-accent-100 flex items-center justify-center">
                    <i className={`${category.icon} text-2xl text-accent-600`}></i>
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{category.name}</h1>
                    <p className="text-sm text-foreground-600 mt-1">{categoryArticles.length} 篇文章</p>
                  </div>
                </div>
                <p className="text-foreground-700 leading-relaxed">{category.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/articles/${article.slug}`}
                    className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
                        <span>{article.date}</span>
                        <span>·</span>
                        <span>{article.author}</span>
                      </div>
                      <h2 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>

              {categoryArticles.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类下暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}